import React, {useEffect, useRef, useState} from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Sunburst from "highcharts/modules/sunburst";
import Accessibility from "highcharts/modules/accessibility";
import {getWeightsIndexDataService} from "../../services/elevate_cpc.service";
import {useMediaQuery} from "@mui/material";
import './GlobalCPC.page.css'
import {GOAL_COLOR_MAPPING} from "../../utils/constants";


const transformMetaDataInitial = (metaData) => {
    const rootId = metaData.index?.["IndexL1 (Id)"] || "root";
    return [
        {
            id: rootId,
            parent: "",
            name: metaData.index?.["IndexL1 (Name)"] || "",
            longName: metaData.index?.["IndexL1 (Name) Long"] || "",
            desc: metaData.index?.["IndexL1 (Description)"] || "",
            color: "#FFFFFF",
            type: "composite",
        },
        ...Object.keys(metaData.goals).flatMap((goalKey) => {
            const goal = metaData.goals[goalKey];
            return [
                {
                    id: goalKey,
                    parent: rootId,
                    name: goal["IndexL2 (Name)"],
                    longName: goal["IndexL2 (Name) Long"] || "",
                    desc: goal["IndexL2 (Description)"] || "",
                    color: GOAL_COLOR_MAPPING[goalKey] || "#CCCCCC",
                    value: 100,
                    type: "stakeholder",
                },
                ...Object.keys(goal.targets).map((targetKey) => {
                    const target = goal.targets[targetKey];
                    return {
                        id: targetKey,
                        parent: goalKey,
                        name: target["IndexL4 (Name)"],
                        longName: target["IndexL4 (Name) Long"] || "",
                        desc: target["IndexL4 (Description)"] || "",
                        color: GOAL_COLOR_MAPPING[goalKey] || "#CCCCCC",
                        value: 90,
                        type: "target",
                    };
                }),
            ];
        }),
    ];
};

const getIndicatorsForTarget = (metaData, targetId) => {
    for (const goalKey of Object.keys(metaData.goals)) {
        const goal = metaData.goals[goalKey];
        if (goal.targets[targetId]) {
            const target = goal.targets[targetId];
            return Object.keys(target.indicators).map((indicatorKey) => ({
                id: indicatorKey,
                parent: targetId,
                name: target.indicators[indicatorKey]["IndexL6 (Name)"],
                longName: target.indicators[indicatorKey]["IndexL6 (Name) Long"],
                desc: target.indicators[indicatorKey]["IndexL6 (Description)"] || "",
                color: GOAL_COLOR_MAPPING[goalKey] || "#CCCCCC",
                value: 25,
                type: "indicator",
            }));
        }
    }
    return [];
};

const SunburstHighChart = ({setSelectedNodeData,  selectedNodeData}) => {
    const [chartData, setChartData] = useState(null);
    const [fullMetaData, setFullMetaData] = useState(null);
    const [expandedTargets, setExpandedTargets] = useState(new Set());
    const chartRef = useRef(null);
    const isMobile = useMediaQuery("(max-width:600px)");
    const [chartWidth, setChartWidth] = useState(isMobile ? 325 : 750);
    const [chartHeight, setChartHeight] = useState(isMobile ? 380 : 750);

    const didFetchWeightsIndexData = useRef(false);

    useEffect(() => {
        if (didFetchWeightsIndexData.current) return;
        didFetchWeightsIndexData.current = true;
        const fetchData = async () => {
            try {
                const response = await getWeightsIndexDataService();
                console.log("Response:", response);

                if (response.data.metaData) {
                    setFullMetaData(response.data.metaData);
                    const initialData = transformMetaDataInitial(response.data.metaData);
                    setChartData(initialData);

                    //  Set root node details by default
                    const rootId = response.data.metaData.index?.["IndexL1 (Id)"];
                    if (rootId) {
                        setSelectedNodeData({
                            id: rootId,
                            name: response.data.metaData.index?.["IndexL1 (Name) Long"] || "",
                            desc: response.data.metaData.index?.["IndexL1 (Description)"] || "",
                            type: "composite",
                        });
                    }
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        setChartWidth(chartWidth);
        setChartHeight(chartHeight);
    }, [isMobile]);

    // helper functions to get names, ids, type, desc of clicked nodes

    const getNodeType = (id, metaData) => {
        // Stakeholders are top-level and GCCR
        if (metaData?.goals?.[id]) {
            const goal = metaData.goals[id];

            // Check if targets exist
            const targets = goal.targets || {};
            const targetValues = Object.values(targets);

            // GCCR case: targets exist, but none of them have indicators
            const isGCCR = targetValues.length > 0 && targetValues.every(t => !t.indicators || Object.keys(t.indicators).length === 0);

            if (isGCCR) {
                return "GCCR";
            }
            return "stakeholder";
        }


        // Targets
        for (const stakeholder of Object.values(metaData.goals || {})) {
            // GCCR indicators (targets with no indicators inside them)
            if (stakeholder.targets?.[id] && (!stakeholder.targets[id].indicators || Object.keys(stakeholder.targets[id].indicators).length === 0)) {
                return "GCCR_indicator";
            }

            // Stakeholder Target
            if (stakeholder.targets?.[id]) {
                return "target";
            }

            // Indicators (inside targets)
            for (const target of Object.values(stakeholder.targets || {})) {
                if (target.indicators?.[id]) {
                    return "indicator";
                }
            }
        }

        return "composite";
    };

    const getDisplayId = (id, nodeType, metaData) => {
        if (!metaData) return id;

        if (nodeType === "composite") {
            return metaData.index?.["IndexL1 (Id)"] || id;
        }

        if (nodeType === "stakeholder" || nodeType === "GCCR") {
            const goal = metaData.goals[id];
            return goal?.["IndexL2 (Id)"] || id;
        }

        if (nodeType === "target" || nodeType === "GCCR_indicator") {
            for (const stakeholder of Object.values(metaData.goals || {})) {
                const target = stakeholder.targets?.[id];
                if (target) {
                    return target["IndexL4 (Id)"] || id;
                }
            }
        }

        if (nodeType === "indicator") {
            for (const stakeholder of Object.values(metaData.goals || {})) {
                for (const target of Object.values(stakeholder.targets || {})) {
                    const indicator = target.indicators?.[id];
                    if (indicator) {
                        return indicator["IndexL6 (Id)"] || id;
                    }
                }
            }
        }

        return id;
    };

    const getNodeName = (id, type, metaData, fallbackName) => {
        if (!metaData) return fallbackName || "";

        if (type === "composite"){
            return metaData.index?.["IndexL1 (Name) Long"]
                || metaData.index?.["IndexL1 (Name)"]
                || fallbackName;
        }

        if (type === "stakeholder" || type === "GCCR") {
            const goal = metaData.goals[id];
            return goal?.["IndexL2 (Name) Long"] || goal?.["IndexL2 (Name)"] || fallbackName;
        }

        if (type === "target" || type === "GCCR_indicator") {
            for (const stakeholder of Object.values(metaData.goals || {})) {
                const target = stakeholder.targets?.[id];
                if (target) {
                    return target["IndexL4 (Name) Long"] || target["IndexL4 (Name)"] || fallbackName;
                }
            }
        }

        if (type === "indicator") {
            for (const stakeholder of Object.values(metaData.goals || {})) {
                for (const target of Object.values(stakeholder.targets || {})) {
                    const indicator = target.indicators?.[id];
                    if (indicator) {
                        return indicator["IndexL6 (Name) Long"] || indicator["IndexL6 (Name)"] || fallbackName;
                    }
                }
            }
        }

        return "";
    };

    const getNodeDesc = (id, metaData) => {
        if (metaData?.index?.["IndexL1 (Id)"] === id) {
            return metaData.index?.["IndexL1 (Description)"] || "";
        }

        // Stakeholder (goal) descriptions
        if (metaData?.goals?.[id]) {
            return metaData.goals[id]["IndexL2 (Description)"] || "";
        }

        // Target + indicator descriptions
        for (const stakeholder of Object.values(metaData.goals || {})) {
            for (const [targetId, target] of Object.entries(stakeholder.targets)) {
                if (targetId === id) {
                    return target["IndexL4 (Description)"] || "";
                }
                for (const [indicatorId, indicator] of Object.entries(target.indicators)) {
                    if (indicatorId === id) {
                        return indicator["IndexL6 (Description)"] || "";
                    }
                }
            }
        }

        return "";
    };

    const handlePointClick = (point) => {
        const rootId = fullMetaData?.index?.["IndexL1 (Id)"];
        const nodeType = getNodeType(point.id, fullMetaData);
        const displayNodeDesc = getNodeDesc(point.id, fullMetaData);
        const displayId = getDisplayId(point.id, nodeType, fullMetaData);
        const displayName = getNodeName(point.id, nodeType, fullMetaData, point.name);

        let GCCRIndicatorData = {};

        // If it's a GCCR indicator → attach label & definition
        if (nodeType === "GCCR_indicator") {
            for (const stakeholder of Object.values(fullMetaData.goals || {})) {
                const target = stakeholder.targets?.[point.id];
                if (target) {
                    GCCRIndicatorData = {
                        label: (target["IndexL4 (Definition)"]?.[0]?.label),
                        definitions: target["IndexL4 (Definition)"] || [],
                    };

                    setSelectedNodeData(GCCRIndicatorData);
                    console.log("Indicator Label:", GCCRIndicatorData.label);
                }
            }
        }

        setSelectedNodeData({
            id: point.id,
            displayId: displayId,
            name: displayName,
            desc: displayNodeDesc,
            type: nodeType,
            ...GCCRIndicatorData,
        });

        // If center item (level 0) or first level (goals) is clicked, reset to original position
        if (point.id === rootId || (point.id.includes('.') && point.id.split('.').length === 1)) {
            // Reset to original state
            setExpandedTargets(new Set());
            if (fullMetaData) {
                const initialData = transformMetaDataInitial(fullMetaData);
                setChartData(initialData);
            }

            // Reset chart view to root level
            const chart = chartRef.current?.chart;
            if (chart && chart.series[0].rootNode) {
                chart.series[0].setRootNode('', true);
            }
            return;
        }

        // Check if this is a target (level 2) and toggle indicators
        if (fullMetaData && point.id.includes('.') && point.id.split('.').length === 2) {
            const targetId = point.id;
            const newExpandedTargets = new Set(expandedTargets);

            if (expandedTargets.has(targetId)) {
                // Collapse: remove indicators for this target
                newExpandedTargets.delete(targetId);
            } else {
                // Expand: add indicators for this target
                newExpandedTargets.add(targetId);
            }

            setExpandedTargets(newExpandedTargets);

            // Rebuild chart data
            const baseData = transformMetaDataInitial(fullMetaData, {
                rootId: targetId,
            });

            const indicatorData = getIndicatorsForTarget(fullMetaData, targetId);

            setChartData([...baseData, ...indicatorData]);
        }
    };

    if (!chartData) return <div>Loading chart...</div>;

    const options = {
        chart: {
            height: chartHeight,
            width: chartWidth
        },
        title: {
            text: "",
        },
        accessibility: {
            enabled: true,
        },
        series: [
            {
                type: "sunburst",
                name: " ",
                data: chartData.map(node => {
                    const isSelected = node.id === selectedNodeData?.id;
                    const activeNode = node.type === "indicator" || node.type === "target";
                    return {
                        ...node,
                        color: (isSelected && activeNode)
                            ? Highcharts.color(node.color).brighten(0.2).get()
                            : node.color,
                    };
                }),
                allowTraversingTree: true,
                borderRadius: 3,
                cursor: "pointer",
                // innerSize: "1%",
                dataLabels: {
                    useHTML: true,
                    formatter: function () {
                        return this.point.name;
                    },
                    style: {
                        textAlign: "center",
                        // fontSize: "12px",
                        color: "white",
                    },
                    filter: {
                        property: "innerArcLength",
                        operator: ">",
                        value: 16,
                    },
                },
                point: {
                    events: {
                        click: function () {
                            handlePointClick(this);
                        },
                    },
                },
                levels: [
                    {
                        level: 1,
                        levelIsConstant: false,
                        dataLabels: {
                            style: {
                                fontSize: isMobile ? "15px" : "28px",
                                textAlign: "center",
                                fontWeight: "bold",
                                color: "black",
                            },
                            filter: {
                                property: "outerArcLength",
                                operator: ">",
                                value: 80,
                            },
                        },
                    },
                    {
                        level: 2,
                        colorByPoint: true,
                        dataLabels: {
                            style: {
                                fontSize: isMobile ? "13px" : "18px",
                                textAlign: "center",
                                color: "white",
                            },
                            crop: false,
                            filter: {
                                property: "outerArcLength",
                                operator: ">",
                                value: 90,
                            },
                        },
                    },
                    {
                        level: 3,
                        colorVariation: {
                            key: "brightness",
                            to: -0.5,
                        },
                        dataLabels: {
                            style: {
                                fontSize: isMobile ? "11px" : "13px",
                                color: "white",
                            },
                            crop: false,
                        },
                    },
                    {
                        level: 4,
                        colorVariation: {
                            key: "brightness",
                            to: 0.5,
                        },
                        dataLabels: {
                            style: {
                                fontSize: isMobile ? "10px" : "12px",
                                color: "white",
                            },
                            crop: false,
                            filter: {
                                property: "outerArcLength",
                                operator: ">",
                                value: 90,
                            },
                        },
                    },
                ],
            },
        ],
        tooltip: {
            headerFormat: "",
            outside: true,
            pointFormat:
                "{point.name}",
        },
    };

    return (
        <HighchartsReact
            ref={chartRef}
            highcharts={Highcharts}
            options={options}
        />
    );
};

export default SunburstHighChart;
